import {
  AnyFundingRequest,
  FundingRequestProvider,
  FundingRequestType,
  IMohClaimFundingRequest,
  IMohFormDisplay,
  IPatient,
  IPractice,
  isMohClaimFundingRequestData,
  MohFormDisplayLabel,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  AtLeast,
  IReffable,
  isWithRef,
  undeletedQuery,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { Practice } from '../../practice/practice';
import { FundingRequest } from '../funding-request';

export class MohClaimFundingRequest {
  static init(
    overrides: AtLeast<IMohClaimFundingRequest, 'data' | 'patient'>
  ): IMohClaimFundingRequest {
    return FundingRequest.init({
      fundingProvider: FundingRequestProvider.MOH,
      type: FundingRequestType.Claim,
      ...overrides,
    });
  }

  static all$(
    practice: WithRef<IPractice>
  ): Observable<WithRef<IMohClaimFundingRequest>[]> {
    return all$(
      undeletedQuery(
        Practice.fundingRequestCol<IMohClaimFundingRequest>(practice),
        where('fundingProvider', '==', FundingRequestProvider.MOH),
        where('type', '==', FundingRequestType.Claim)
      )
    );
  }

  static byPatient$(
    practice: IReffable<IPractice>,
    patient: IReffable<IPatient>
  ): Observable<WithRef<IMohClaimFundingRequest>[]> {
    return all$(
      undeletedQuery(
        Practice.fundingRequestCol<IMohClaimFundingRequest>(practice),
        where('fundingProvider', '==', FundingRequestProvider.MOH),
        where('type', '==', FundingRequestType.Claim),
        where('patient.ref', '==', patient.ref)
      )
    );
  }

  static formDisplays(request: IMohClaimFundingRequest): IMohFormDisplay[] {
    return [
      {
        formPath: request.data.individualTreatmentFormPath,
        label: MohFormDisplayLabel.IndividualTreatment,
      },
      {
        formPath: request.data.claimSummaryFormPath,
        label: MohFormDisplayLabel.ClaimSummary,
      },
    ].filter((form): form is IMohFormDisplay => !!form.formPath);
  }
}

export function isMohClaimFundingRequest(
  fundingRequest: WithRef<AnyFundingRequest>
): fundingRequest is WithRef<IMohClaimFundingRequest> {
  return (
    isWithRef(fundingRequest) &&
    fundingRequest.fundingProvider === FundingRequestProvider.MOH &&
    fundingRequest.type === FundingRequestType.Claim &&
    isMohClaimFundingRequestData(fundingRequest.data)
  );
}
